/**
 * Analytics Filtering Service
 * Mirrors backend filtering logic for consistent client/server-side filtering
 * Supports entity aliasing and comprehensive filter matching
 */

const FILTER_CONTEXT_KEYS = [
	"user_id",
	"user_name",
	"user_email",
	"customer_id",
	"customer_name",
	"site_id",
	"site_name",
	"legal_entity",
	"machine_id",
	"machine_name",
	"machine_number",
	"location",
	"user_role",
];

/**
 * Normalize a value for comparison (trim, lowercase)
 */
const normalizeFilterValue = (value) => String(value ?? "").trim().toLowerCase();

/**
 * Check if a value represents "all" or empty selection.
 * Also accepts an array (multi-select filters): an empty array, or an array
 * containing only "all"/empty entries, counts as "all".
 */
const isAllValue = (value) => {
	if (Array.isArray(value)) {
		return value.length === 0 || value.every((item) => isAllValue(item));
	}
	return value === undefined || value === null || value === "" || value === "All";
};

/**
 * Match a context value against a filter value with normalization
 * @param {string|number} contextValue - Value from event context
 * @param {string|number} filterValue - User-selected filter value
 * @returns {boolean} Whether they match (case-insensitive, trimmed)
 */
const matchesValue = (contextValue, filterValue) => {
	const normalized = normalizeFilterValue(filterValue);
	if (!normalized) return true; // Empty filter matches everything
	const contextNormalized = normalizeFilterValue(contextValue);
	return contextNormalized === normalized;
};

/**
 * Match an entity (ID + name aliasing)
 * Supports matching by ID OR by name
 * @param {Object} options - { idKey, idValue, aliases }
 * @param {Object} context - Event context object
 * @returns {boolean} Whether entity matches
 */
const matchesEntity = (options, context = {}) => {
	const { idKey, idValue, aliases = [] } = options;

	// If specific ID is provided, try to match it
	if (idValue) {
		const contextId = normalizeFilterValue(context[idKey]);
		if (contextId) return contextId === idValue;
		// Fall back to alias matching if ID not found in context
		return aliases.some(([contextKey, value]) => value && matchesValue(context[contextKey], value));
	}

	// No ID filter - check if any active aliases match
	const activeAliases = aliases.filter(([, value]) => value);
	if (!activeAliases.length) return true;
	return activeAliases.some(([contextKey, value]) => matchesValue(context[contextKey], value));
};

/**
 * Main filtering function - checks if an event context matches all filters
 * Implements entity aliasing for customer, site, and machine
 * @param {Object} context - Event context with user/customer/site/machine data
 * @param {Object} filters - Applied filters object
 * @returns {boolean} Whether context matches all active filters
 */
const matchesFilters = (context = {}, filters = {}) => {
	const normalized = Object.entries(filters).reduce((acc, [key, value]) => {
		const nextValue = normalizeFilterValue(value);
		if (nextValue) acc[key] = nextValue;
		return acc;
	}, {});

	// No active filters - everything matches
	if (Object.keys(normalized).length === 0) return true;

	// Check each entity with aliasing support
	return (
		matchesEntity(
			{
				idKey: "customer_id",
				idValue: normalized.customer_id,
				aliases: [["customer_name", normalized.customer_name]],
			},
			context
		) &&
		matchesEntity(
			{
				idKey: "site_id",
				idValue: normalized.site_id,
				aliases: [["site_name", normalized.site_name]],
			},
			context
		) &&
		matchesEntity(
			{
				idKey: "machine_id",
				idValue: normalized.machine_id,
				aliases: [
					["machine_name", normalized.machine_name],
					["machine_number", normalized.machine_number],
				],
			},
			context
		) &&
		matchesValue(context.user_email, normalized.user_email) &&
		matchesValue(context.legal_entity, normalized.legal_entity) &&
		matchesValue(context.location, normalized.location) &&
		matchesValue(context.user_role, normalized.user_role)
	);
};

/**
 * Filter array of items (with context property) by filters
 * @param {Array} items - Items with context property
 * @param {Object} filters - Filters to apply
 * @returns {Array} Filtered items
 */
const filterItems = (items = [], filters = {}) => {
	return items.filter((item) => matchesFilters(item?.context || item, filters));
};

/**
 * Build available filter options from items
 * @param {Array} items - Items with context property
 * @returns {Object} Available options per filter key
 */
const buildAvailableFilterOptions = (items = []) => {
	const options = {
		user_email: new Set(),
		customer_id: new Set(),
		customer_name: new Set(),
		site_id: new Set(),
		site_name: new Set(),
		legal_entity: new Set(),
		machine_id: new Set(),
		location: new Set(),
		user_role: new Set(),
	};

	items.forEach((item) => {
		const context = item?.context || item;
		Object.entries(options).forEach(([key, set]) => {
			const value = context[key];
			if (value) set.add(String(value));
		});
	});

	const uniqueSorted = (values = []) =>
		Array.from(new Set(values.filter(Boolean).map(String))).sort((a, b) =>
			a.localeCompare(b, undefined, { sensitivity: "base" })
		);

	return {
		user_email: uniqueSorted(Array.from(options.user_email)),
		customer_id: uniqueSorted(Array.from(options.customer_id)),
		customer_name: uniqueSorted(Array.from(options.customer_name)),
		site_id: uniqueSorted(Array.from(options.site_id)),
		site_name: uniqueSorted(Array.from(options.site_name)),
		legal_entity: uniqueSorted(Array.from(options.legal_entity)),
		machine_id: uniqueSorted(Array.from(options.machine_id)),
		location: uniqueSorted(Array.from(options.location)),
		user_role: uniqueSorted(Array.from(options.user_role)),
	};
};

/**
 * Validate that filters match available options
 * @param {Object} filters - Current filters
 * @param {Object} availableOptions - Available options per filter key
 * @returns {Object} Validated filters (invalid ones reset to "")
 */
const validateFilters = (filters = {}, availableOptions = {}) => {
	const validated = { ...filters };

	Object.entries(filters).forEach(([key, value]) => {
		if (isAllValue(value)) return; // "All" is always valid

		const availableForKey = availableOptions[key] || [];
		const isValid = availableForKey.some(
			(option) => normalizeFilterValue(option) === normalizeFilterValue(value)
		);

		if (!isValid) {
			validated[key] = ""; // Reset invalid filter
		}
	});

	return validated;
};

/**
 * Get active filters (exclude "All" values)
 * @param {Object} filters - All filters
 * @returns {Object} Only active/non-All filters
 */
const getActiveFilters = (filters = {}) => {
	return Object.entries(filters).reduce((acc, [key, value]) => {
		if (!isAllValue(value)) {
			acc[key] = value;
		}
		return acc;
	}, {});
};

/**
 * Count how many filters are active
 */
const getActiveFilterCount = (filters = {}) => {
	return Object.values(filters).filter((value) => !isAllValue(value)).length;
};

/**
 * Build a filter summary for display
 * @param {Object} filters - Active filters
 * @returns {string} Human-readable summary
 */
const buildFilterSummary = (filters = {}) => {
	const active = getActiveFilters(filters);
	const entries = Object.entries(active);

	if (entries.length === 0) return "No filters applied";

	return entries
		.map(([key, value]) => {
			const keyLabel = key
				.replace(/_/g, " ")
				.replace(/^\w/, (c) => c.toUpperCase());
			return `${keyLabel}: ${value}`;
		})
		.join(", ");
};

export {
	matchesFilters,
	matchesValue,
	matchesEntity,
	filterItems,
	buildAvailableFilterOptions,
	validateFilters,
	getActiveFilters,
	getActiveFilterCount,
	buildFilterSummary,
	normalizeFilterValue,
	isAllValue,
	FILTER_CONTEXT_KEYS,
};
